import { DocumentsSection } from './DocumentsSection'
import { useState, useEffect, useRef } from 'react'
import { Link } from 'wouter'
import {
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  FileText,
  Landmark,
  LineChart,
  Network,
  Sparkles,
  UsersRound,
  Clock,
  RotateCcw,
} from 'lucide-react'
import { WaveDivider } from '../components/WaveDivider'
import { AvisoBanner } from '../components/AvisoBanner'
import { api } from '../services/api'
import type { SiteContentMap, HeroContent, ChallengeContent, ScheduleItem, AvisoContent } from '../services/api'

// ==================== DADOS ====================

export const challenges = [
  { 
    id: '01', 
    icon: <Building2 size={22} className="text-[#0d6b3d]" />, 
    title: 'Licenciamento Urbano Ágil e Integrado', 
    question: 'Como integrar e automatizar o processo de licenciamento urbano para que empreendedores tenham acesso a um sistema transparente e ágil?', 
    result: 'Reduzir o tempo médio de licenciamento de 280 para menos de 60 dias.' 
  },
  { 
    id: '02', 
    icon: <LineChart size={22} className="text-[#0d6b3d]" />, 
    title: 'Arrecadação Inteligente e Sustentável', 
    question: 'Como modernizar os processos de arrecadação para ampliar receita própria e fortalecer a sustentabilidade fiscal?', 
    result: 'Apoiar aumento de arrecadação própria, redução de inadimplência e criação de base cadastral integrada.' 
  },
  { 
    id: '03', 
    icon: <UsersRound size={22} className="text-[#0d6b3d]" />, 
    title: 'Participação Cidadã e Transparência na Gestão', 
    question: 'Como fortalecer a participação cidadã na gestão urbana e ambiental, promovendo corresponsabilidade entre governo e sociedade?', 
    result: 'Criar espaços digitais participativos, ampliar transparência e reduzir tempo de resposta a demandas cidadãs.' 
  },
]

export const schedule = [
  { date: '17/08/2026', stage: 'Publicação do edital', note: 'Abertura oficial da consulta pública ao mercado.' },
  { date: '25/09 a 26/10/2026', stage: 'Recebimento de contribuições (prazo prorrogado)', note: 'Envio de informações técnicas, experiências, propostas e sugestões pelo formulário eletrônico.' },
  { date: '15/10/2026 • 14h às 17h', stage: 'Reunião de imersão (nova data)', note: 'Audiência pública online ou híbrida com especialistas dos desafios.' },
  { date: 'A definir', stage: 'Sessão técnica reservada', note: 'Restrita aos inscritos que assinarem o Termo de Confidencialidade, dentro do novo período de recebimento.' },
  { date: '30/10/2026', stage: 'Publicação das submissões válidas', note: 'Divulgação das contribuições aceitas e consolidação para análise técnica.' },
  { date: '09/11/2026', stage: 'Publicação do relatório consolidado', note: 'Publicação do relatório consolidado da Consulta Pública.' },
]

export const defaultAviso: AvisoContent = {
  enabled: true,
  title: 'Aviso de Prorrogação — Consulta Pública ao Mercado nº 001/2026/SMTI',
  message: 'O período de recebimento de contribuições fica reaberto e prorrogado por 30 dias, passando a vigorar de 25/09/2026 a 26/10/2026. Contribuições já enviadas entre 17/08 e 16/09/2026 permanecem válidas e podem ser complementadas ou substituídas até o novo prazo. Será realizada nova reunião de imersão em 15/10/2026, das 14h às 17h.',
  publishNote: 'Este aviso será publicado no Diário Oficial do Município de Porto Velho até 25/09/2026.',
}

export const defaultMensagemEncerramento =
  'O período de recebimento de contribuições para esta consulta pública foi encerrado. Agradecemos a todos que participaram. Acompanhe no cronograma as próximas etapas.'

export function resolveAviso(content: SiteContentMap): AvisoContent {
  if (content.inscricoes?.aberta === false) {
    return {
      enabled: true,
      title: 'Inscrições encerradas — Consulta Pública ao Mercado nº 001/2026/SMTI',
      message: content.inscricoes.mensagem?.trim() || defaultMensagemEncerramento,
      publishNote: '',
    }
  }
  return content.aviso || defaultAviso
}

export const legalBases = [
  'Lei Complementar Municipal nº 1.061/2026 — Política Municipal de Ciência, Tecnologia e Inovação de Porto Velho',
  'Lei Complementar nº 182/2021 — Marco Legal das Startups',
  'Lei nº 10.973/2004 — Lei de Inovação',
  'Lei nº 14.133/2021 — Licitações e Contratos Administrativos',
  'Lei nº 13.709/2018 — Lei Geral de Proteção de Dados',
]

// ==================== HOOKS ====================

function useScrollAnimation(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(element)
        }
      },
      { threshold }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [threshold])

  return { ref, isVisible }
}

// ==================== SEÇÕES ====================

function HeroSection({ content }: { content?: HeroContent }) {
  const { ref, isVisible } = useScrollAnimation(0.1)

  const badge = content?.badge || 'Prefeitura Municipal de Porto Velho • Sebrae'
  const title = content?.title || 'Catalisa GOV'
  const subtitle = content?.subtitle || 'Porto Velho'
  const description = content?.description ||
    'Uma jornada institucional para aproximar a Prefeitura, o Sebrae, o ecossistema de inovação e a sociedade na construção de soluções para desafios públicos municipais.'

  return (
    <section id="programa" className="relative min-h-[85vh] flex items-center overflow-hidden" aria-label="Seção principal Catalisa GOV">
      <div className="absolute inset-0">
        <img
          src="https://d2xsxph8kpxj0f.cloudfront.net/310419663030643880/WieEhnyjBAToAyi933qoua/catalisa-hero-porto-velho-Z2Dp7bBuW2jPwz82KxzjHq.webp"
          alt="Catalisa GOV Porto Velho - Inovação Pública"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b2e46]/90 via-[#0b2e46]/80 to-[#0b2e46]/60" />
      </div>

      <div ref={ref} className="container relative z-10 py-20 md:py-28">
        <div className={`max-w-2xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <Sparkles size={14} className="text-[#f1b90f]" />
            <span className="text-white/90 text-xs font-medium">{badge}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
            {title}
            <span className="block text-[#f1b90f] text-xl sm:text-2xl md:text-3xl mt-1 font-semibold">{subtitle}</span>
          </h1>
          <p className="text-white/90 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
            {description}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              href="/consulta-publica"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0d6b3d] text-white font-semibold rounded-lg hover:bg-[#0b5b35] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              <FileText size={18} />
              Acessar Consulta Pública
            </Link>
            <a
              href="#desafios"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#f1b90f] text-[#0b2e46] font-semibold rounded-lg hover:bg-[#e0a80e] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Conhecer Desafios
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

function ProgramSection() {
  const { ref, isVisible } = useScrollAnimation()

  const programCards = [
    { icon: <Network size={22} className="text-[#0d6b3d]" />, title: 'Catalisa GOV', desc: 'Metodologia do Sebrae para apoiar a inovação pública e aproximar governo e mercado para transformar desafios municipais em oportunidades.' },
    { icon: <Landmark size={22} className="text-[#0d6b3d]" />, title: 'CPSI', desc: 'Instrumento do Marco Legal das Startups para contratação de testes de soluções inovadoras quando a Administração conhece o desafio.' },
    { icon: <Building2 size={22} className="text-[#0d6b3d]" />, title: 'Inovação Aberta', desc: 'O processo abre diálogo com startups, empresas, academia, cooperativas e organizações sociais para ampliar repertório técnico.' },
    { icon: <LineChart size={22} className="text-[#0d6b3d]" />, title: 'Governança Pública', desc: 'A consulta pública reforça integridade, publicidade, LGPD, acesso à informação e critérios técnicos para futuras contratações.' },
  ]

  return (
    <section id="cpsi" className="py-16 md:py-20 bg-[#f7f9f2]" aria-labelledby="programa-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[#5d7f48] text-xs font-semibold uppercase tracking-wider mb-2">O Programa</span>
            <h2 id="programa-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-[#0d2d47] mb-3">
              Compra pública de inovação com segurança, transparência e foco em problema público.
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
            {programCards.map((card, index) => (
              <div
                key={card.title}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-all duration-300"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-green-50 rounded-lg flex items-center justify-center shadow-sm shrink-0">
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="font-serif font-bold text-base text-[#0d2d47] mb-1">{card.title}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#0b2e46] rounded-xl p-6 md:p-8 text-white">
            <h3 className="font-serif font-bold text-xl mb-4 text-[#f1b90f] text-center">CPSI — Contrato Público de Solução Inovadora</h3>
            <p className="text-white/90 leading-relaxed mb-4 text-center max-w-3xl mx-auto">
              Em vez de iniciar por uma especificação fechada, a Prefeitura pode formular desafios, 
              ouvir o ecossistema, testar soluções em ambiente controlado e avaliar resultados com 
              critérios transparentes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-6">
              {['Escuta pública', 'Qualificação dos desafios', 'Estruturação do CPSI', 'Seleção e teste', 'Avaliação de impacto'].map((item, index) => (
                <div key={item} className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                  <span className="text-[#f1b90f] font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                  <span className="text-xs text-white/80">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function TimelineSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [stages, setStages] = useState<any[]>([])
  const [stagesLoading, setStagesLoading] = useState(true)

  const loadStages = async () => {
    try {
      const data = await api.getAllStages()
      if (data && data.length > 0) {
        setStages(data)
      }
    } catch (error) {
      console.error('Erro ao carregar fases:', error)
    } finally {
      setStagesLoading(false)
    }
  }

  useEffect(() => {
    loadStages()
    window.addEventListener('stages-updated', loadStages)
    return () => window.removeEventListener('stages-updated', loadStages)
  }, [])

  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    'concluido': { bg: 'bg-[#0d6b3d]', text: 'text-white', label: 'Concluído' },
    'em-andamento': { bg: 'bg-[#f1b90f]', text: 'text-gray-900', label: 'Em andamento' },
    'a-iniciar': { bg: 'bg-gray-200', text: 'text-gray-600', label: 'A iniciar' }
  }

  return (
    <section id="etapas" className="py-16 md:py-20 bg-white" aria-labelledby="timeline-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[#5d7f48] text-xs font-semibold uppercase tracking-wider mb-2">Cronograma</span>
            <h2 id="timeline-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-[#0d2d47] mb-3">
              Etapas do Catalisa GOV
            </h2>
            <p className="text-gray-600 text-sm">
              Acompanhe o andamento de cada fase do programa
            </p>
          </div>

          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" aria-hidden="true" />
            <div className="space-y-8">
              {stagesLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0d6b3d]"></div>
                </div>
              ) : stages.length === 0 ? (
                <p className="text-center text-gray-500">Nenhuma etapa cadastrada ainda.</p>
              ) : (
                stages.map((step) => {
                  const config = statusConfig[step.status] || statusConfig['a-iniciar']
                  const isEmAndamento = step.status === 'em-andamento'
                  const isConcluido = step.status === 'concluido'
                  return (
                    <div key={step.id} className="relative flex gap-5">
                      <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center shrink-0 border-4 border-white shadow-md ${
                        isEmAndamento ? 'bg-[#0d6b3d] ring-4 ring-[#0d6b3d]/20' :
                        isConcluido ? 'bg-[#0d6b3d]' : 'bg-gray-300'
                      }`}>
                        <span className="text-white font-bold text-sm">{step.stage}</span>
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-serif font-bold text-base text-[#0d2d47]">{step.title}</h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${config.bg} ${config.text}`}>
                            {isEmAndamento && <span className="w-1.5 h-1.5 bg-current rounded-full mr-1 animate-pulse" />}
                            {config.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mb-1">{step.description}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock size={11} />
                          <span>Duração estimada: {step.duration}</span>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <button
              onClick={loadStages}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-[#0d6b3d] hover:text-[#0b5b35] transition-colors"
            >
              <RotateCcw size={14} />
              Atualizar etapas
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

function ChallengesSection({ content }: { content?: ChallengeContent[] }) {
  const { ref, isVisible } = useScrollAnimation()

  const displayChallenges = challenges.map((challenge) => {
    const override = content?.find((c) => c.id === challenge.id)
    return override ? { ...challenge, title: override.title, question: override.question, result: override.result } : challenge
  })

  return (
    <section id="desafios" className="py-16 md:py-20 bg-[#f7f9f2]" aria-labelledby="desafios-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block text-[#5d7f48] text-xs font-semibold uppercase tracking-wider mb-2">Desafios Prioritários</span>
            <h2 id="desafios-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-[#0d2d47] mb-3">
              Três frentes para modernizar serviços, dados e participação social.
            </h2>
            <p className="text-gray-600 text-sm">
              Os desafios abaixo foram estruturados a partir da minuta de consulta pública e devem orientar 
              contribuições técnicas, ideias, soluções existentes e propostas de teste para futuros CPSI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {displayChallenges.map((challenge) => (
              <div key={challenge.id} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-sm font-bold text-[#5d7f48]">DESAFIO {challenge.id}</span>
                  {challenge.icon}
                </div>
                <h3 className="font-serif font-bold text-lg text-[#0d2d47] mb-2">{challenge.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{challenge.question}</p>
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-xs font-semibold text-[#5d7f48] uppercase tracking-wider">Resultado esperado</p>
                  <p className="text-sm text-gray-700 mt-1">{challenge.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function ConsultaSection({ schedule: scheduleOverride }: { schedule?: ScheduleItem[] }) {
  const { ref, isVisible } = useScrollAnimation()
  const displaySchedule = scheduleOverride && scheduleOverride.length > 0 ? scheduleOverride : schedule

  return (
    <section id="consulta" className="py-16 md:py-20 bg-[#0b2e46] text-white" aria-labelledby="consulta-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <span className="inline-block text-[#f1b90f] text-xs font-semibold uppercase tracking-wider mb-2">Consulta Pública</span>
              <h2 id="consulta-titulo" className="font-serif text-2xl sm:text-3xl font-bold mb-4">
                Fase preparatória para futuros CPSI
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                A consulta pública coleta contribuições técnicas sobre soluções, requisitos, custos, 
                estágios de maturidade, testes, indicadores e riscos. As manifestações subsidiarão 
                documentos futuros, sem gerar obrigação de contratação ou preferência aos participantes.
              </p>
              <div className="space-y-3">
                {[
                  'Startups e empresas inovadoras',
                  'Universidades e institutos de pesquisa',
                  'Cidadãos, organizações sociais e servidores'
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 bg-white/10 rounded-lg p-3">
                    <CheckCircle2 size={18} className="text-[#f1b90f] mt-0.5 shrink-0" />
                    <span className="text-sm text-white/90">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                href="/consulta-publica"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#f1b90f] text-[#0b2e46] font-semibold rounded-lg hover:bg-[#e0a80e] transition-all"
              >
                Participar da Consulta
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="font-serif font-bold text-xl mb-4 text-[#f1b90f]">Cronograma Indicativo</h3>
              <div className="space-y-4">
                {displaySchedule.map((item) => (
                  <div key={item.date} className="border-b border-white/10 pb-3 last:border-0">
                    <div className="flex items-center gap-2 text-xs text-[#f1b90f] font-mono">
                      <CalendarDays size={14} />
                      {item.date}
                    </div>
                    <p className="font-semibold text-sm mt-1">{item.stage}</p>
                    <p className="text-xs text-white/60">{item.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}


function LegalSection({ legalBases: legalBasesOverride }: { legalBases?: string[] }) {
  const { ref, isVisible } = useScrollAnimation()
  const displayLegalBases = legalBasesOverride && legalBasesOverride.length > 0 ? legalBasesOverride : legalBases

  return (
    <section id="base-legal" className="py-16 md:py-20 bg-white" aria-labelledby="legal-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block text-[#5d7f48] text-xs font-semibold uppercase tracking-wider mb-2">Base Legal</span>
            <h2 id="legal-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-[#0d2d47] mb-3">
              Fundamentos para uma jornada pública responsável.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {displayLegalBases.map((base) => (
              <div key={base} className="flex gap-3 bg-[#f7f9f2] rounded-lg p-4 border border-gray-100">
                <FileText size={18} className="text-[#0d6b3d] mt-0.5 shrink-0" />
                <p className="text-sm text-gray-700 leading-relaxed">{base}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ==================== MAIN ====================

export default function Home() {
  const [content, setContent] = useState<SiteContentMap>({})

  useEffect(() => {
    api.getContent().then(setContent)
  }, [])

  return (
    <main id="conteudo-principal">
      <AvisoBanner aviso={resolveAviso(content)} />
      <HeroSection content={content.hero} />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <ProgramSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <TimelineSection />
      <WaveDivider position="bottom" fillColor="#F7F9F2" />
      <ChallengesSection content={content.challenges} />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <DocumentsSection />
      <WaveDivider position="bottom" fillColor="#0B2E46" />
      <ConsultaSection schedule={content.schedule} />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <LegalSection legalBases={content.legalBases} />
    </main>
  )
}